import React from 'react'
import {Document, Font, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'

import logo from '../../../../../_websoft/assets/images/logo.jpg'

Font.register({
  family: 'Roboto',
  fonts: [
    {src: require('../../../../../_websoft/fonts/Roboto/Roboto-Regular.ttf'), fontWeight: 'normal'},
    {src: require('../../../../../_websoft/fonts/Roboto/Roboto-Bold.ttf'), fontWeight: 'bold'},
  ],
})

const PRIMARY = '#1f2937'
const BORDER = '#d1d5db'
const LIGHT = '#f9fafb'
const SECTION_BG = '#e5e7eb'
const TEXT = '#374151'
const MUTED = '#6b7280'
const styles = StyleSheet.create({
  page: {
    paddingTop: 16,
    paddingBottom: 20,
    paddingHorizontal: 22,
    fontSize: 8,
    fontFamily: 'Roboto',
    color: TEXT,
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `2 solid ${PRIMARY}`,
    paddingBottom: 9,
    marginBottom: 10,
  },
  logoWrap: {
    width: '35%',
  },
  logo: {
    width: 135,
  },
  headerRight: {
    width: '65%',
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: PRIMARY,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 7,
    color: MUTED,
  },
  najavaId: {
    fontSize: 14,
    fontWeight: 'bold',
    color: PRIMARY,
    marginTop: 4,
  },
  section: {
    marginBottom: 7,
  },
  sectionTitle: {
    backgroundColor: SECTION_BG,
    color: PRIMARY,
    paddingTop: 3,
    paddingBottom: 3,
    paddingHorizontal: 7,
    fontSize: 9.5,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  col6Left: {
    width: '49%',
    marginRight: '2%',
  },
  col6Right: {
    width: '49%',
  },
  col4Left: {
    width: '32%',
    marginRight: '2%',
  },
  col4Middle: {
    width: '32%',
    marginRight: '2%',
  },
  col4Right: {
    width: '32%',
  },
  col3Left: {
    width: '23.5%',
    marginRight: '2%',
  },
  col3Middle: {
    width: '23.5%',
    marginRight: '2%',
  },
  col3Right: {
    width: '23.5%',
  },
  fieldWrap: {
    border: `1 solid ${BORDER}`,
    backgroundColor: '#fff',
  },
  attentionBox: {
    border: `1.5 solid ${PRIMARY}`,
    backgroundColor: '#f3f4f6',
    paddingTop: 6,
    paddingBottom: 2,
    paddingHorizontal: 6,
  },
  attentionTitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: PRIMARY,
    marginBottom: 5,
  },
  label: {
    fontSize: 6.5,
    color: MUTED,
    borderBottom: `1 solid ${BORDER}`,
    backgroundColor: LIGHT,
    paddingTop: 2,
    paddingBottom: 2,
    paddingHorizontal: 5,
  },
  value: {
    fontSize: 8,
    color: TEXT,
    paddingTop: 4,
    paddingBottom: 4,
    paddingHorizontal: 5,
    minHeight: 14,
  },
  exportValue: {
    fontSize: 13,
    fontWeight: 'bold',
    color: PRIMARY,
    minHeight: 20,
  },
  table: {
    border: `1 solid ${BORDER}`,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: SECTION_BG,
  },
  tableRow: {
    flexDirection: 'row',
    borderTop: `1 solid ${BORDER}`,
    minHeight: 24,
    alignItems: 'stretch',
  },
  tableColNalog: {
    width: '13%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColStavka: {
    width: '8%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColPonuda: {
    width: '13%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColSifra: {
    width: '18%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColPaketi: {
    width: '17%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColNapomena: {
    width: '17%',
    borderRight: `1 solid ${BORDER}`,
    justifyContent: 'center',
  },
  tableColIstovar: {
    width: '14%',
    justifyContent: 'center',
  },
  th: {
    color: PRIMARY,
    fontSize: 10,
    fontWeight: 'bold',
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
  },
  td: {
    fontSize: 10,
    color: TEXT,
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
  },
  notesBox: {
    border: `1 solid ${BORDER}`,
    minHeight: 40,
    paddingTop: 5,
    paddingBottom: 5,
    paddingHorizontal: 6,
  },
  footer: {
    position: 'absolute',
    bottom: 8,
    left: 22,
    right: 22,
    borderTop: `1 solid ${BORDER}`,
    paddingTop: 3,
    textAlign: 'center',
    fontSize: 6.5,
    color: '#9ca3af',
  },
})

type PdfFieldProps = {
  label: string
  value?: string
  valueStyle?: any
}

const PdfField: React.FC<PdfFieldProps> = ({label, value, valueStyle}) => (
  <View style={styles.fieldWrap}>
    <Text style={styles.label}>{label}</Text>
    <Text style={[styles.value, valueStyle]}>{value || '-'}</Text>
  </View>
)

type Props = {
  data: any
}

const getValue = (item: any, keys: string[], fallback: any = '-') => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== '') {
      return item[key]
    }
  }

  return fallback
}

const getNestedValue = (item: any, path: string) => {
  return path.split('.').reduce((value, key) => value?.[key], item)
}

const getBojaValue = (item: any) => {
  const flatBoja = getValue(item, ['boja', 'Boja', 'bojaArtikla', 'BojaArtikla'], '')

  const bojaGore =
    getNestedValue(item, 'proizvodnja.boja_gore.BojaNaziv') ??
    getNestedValue(item, 'proizvodnja.boja_gore.naziv') ??
    getNestedValue(item, 'proizvodnja.boja_gore.Naziv') ??
    getNestedValue(item, 'proizvodnja.bojaGore.BojaNaziv') ??
    getNestedValue(item, 'proizvodnja.bojaGore.naziv') ??
    getNestedValue(item, 'proizvodnja.bojaGore.Naziv') ??
    getNestedValue(item, 'proizvodnja.boja_bore.BojaNaziv') ??
    getNestedValue(item, 'proizvodnja.boja_bore.naziv') ??
    getNestedValue(item, 'boja_gore.BojaNaziv') ??
    getNestedValue(item, 'boja_gore.naziv') ??
    getNestedValue(item, 'boja_gore.Naziv') ??
    getNestedValue(item, 'bojaGore.BojaNaziv') ??
    getNestedValue(item, 'bojaGore.naziv') ??
    getNestedValue(item, 'bojaGore.Naziv') ??
    getNestedValue(item, 'boja_bore.BojaNaziv') ??
    getNestedValue(item, 'boja_bore.naziv')

  const bojaDole =
    getNestedValue(item, 'proizvodnja.boja_dole.BojaNaziv') ??
    getNestedValue(item, 'proizvodnja.boja_dole.naziv') ??
    getNestedValue(item, 'proizvodnja.boja_dole.Naziv') ??
    getNestedValue(item, 'proizvodnja.bojaDole.BojaNaziv') ??
    getNestedValue(item, 'proizvodnja.bojaDole.naziv') ??
    getNestedValue(item, 'proizvodnja.bojaDole.Naziv') ??
    getNestedValue(item, 'boja_dole.BojaNaziv') ??
    getNestedValue(item, 'boja_dole.naziv') ??
    getNestedValue(item, 'boja_dole.Naziv') ??
    getNestedValue(item, 'bojaDole.BojaNaziv') ??
    getNestedValue(item, 'bojaDole.naziv') ??
    getNestedValue(item, 'bojaDole.Naziv')

  if (bojaGore || bojaDole) {
    return `${bojaGore ?? '-'} / ${bojaDole ?? '-'}`
  }

  if (flatBoja && flatBoja !== '-' && flatBoja !== '- / -') {
    return flatBoja
  }

  return '-'
}

const getSifraValue = (item: any) => {
  return (
    getNestedValue(item, 'proizvodnja.artikal') ??
    getValue(item, ['sifra', 'Sifra', 'sifraArtikla', 'SifraArtikla', 'artikal', 'Artikal'])
  )
}

const getDodatniUtovar = (value: any) => {
  if (value === true || value === 1 || value === '1' || String(value).toUpperCase() === 'DA') {
    return 'DA'
  }

  if (value === false || value === 0 || value === '0' || String(value).toUpperCase() === 'NE') {
    return 'NE'
  }

  return value || '-'
}

const formatPdfDate = (value: any) => {
  if (!value) {
    return '-'
  }

  const dateValue = String(value).split('T')[0]
  const [year, month, day] = dateValue.split('-')

  if (year && month && day) {
    return `${day}-${month}-${year}`
  }

  return String(value)
}

const NajavaUtovaraPdf: React.FC<Props> = ({data}) => {
  const vozacFirma = getValue(data, ['VozacFirma', 'vozacFirma'], '')
  const fakturisatiTransport = getDodatniUtovar(
    getValue(data, ['FakturisatiTransport', 'fakturisatiTransport'], false)
  )
  const fakturisatiTransportNalog = String(
    getValue(data, ['FakturisatiTransportNalog', 'fakturisatiTransportNalog'], '')
  ).trim()

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoWrap}>
            <Image src={logo} style={styles.logo} />
          </View>

          <View style={styles.headerRight}>
            <Text style={styles.title}>Najava utovara</Text>
            <Text style={styles.subtitle}>Dokument za organizaciju transporta i isporuke robe</Text>
            {data.idNajaveUtovara && (
              <Text style={styles.najavaId}>ID najave utovara: {data.idNajaveUtovara}</Text>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INFORMACIJE O KUPCU</Text>
          <View style={styles.row}>
            <View style={styles.col6Left}>
              <PdfField label='Naziv kupca' value={data.kupac} />
            </View>
            <View style={styles.col6Right}>
              <PdfField label='Kontakt osoba i telefon' value={data.kontaktOsoba} />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View style={styles.col6Left}>
              <PdfField
                label='Adresa Istovara 1 / Kontakt osoba'
                value={[data.istovar1, data.istovar1Kontakt].filter(Boolean).join(' / ')}
              />
            </View>
            <View style={styles.col6Right}>
              <PdfField
                label='Adresa Istovara 2 / Kontakt osoba'
                value={[data.istovar2, data.istovar2Kontakt].filter(Boolean).join(' / ')}
              />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INFORMACIJE O PREVOZU</Text>
          <View style={styles.row}>
            <View style={styles.col3Left}>
              <PdfField label='Ime i prezime vozaca' value={data.vozacIme} />
            </View>
            <View style={styles.col3Middle}>
              <PdfField label='Vozac Firma' value={vozacFirma} />
            </View>
            <View style={styles.col3Middle}>
              <PdfField label='Tel vozaca' value={data.vozacTel} />
            </View>
            <View style={styles.col3Right}>
              <PdfField
                label='Reg oznaka kamiona / prikolice'
                value={`${data.regKamion || ''} / ${data.regPrikolica || ''}`}
              />
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col3Left}>
              <PdfField label='Datum utovara' value={formatPdfDate(data.datumUtovara)} />
            </View>
            <View style={styles.col3Middle}>
              <PdfField label='Granicni prelaz' value={data.granicniPrelaz} />
            </View>
            <View style={styles.col3Middle}>
              <PdfField label='Broj licne karte / pasosa' value={data.brojPasosa} />
            </View>
            <View style={styles.col3Right}>
              <PdfField label='E-mail dokumentacija' value={data.emailDokumentacija} />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <View style={styles.tableColNalog}>
                <Text style={styles.th}>Nalog</Text>
              </View>
              <View style={styles.tableColStavka}>
                <Text style={styles.th}>Stavka</Text>
              </View>
              <View style={styles.tableColPonuda}>
                <Text style={styles.th}>Ponuda</Text>
              </View>
              <View style={styles.tableColSifra}>
                <Text style={styles.th}>Sifra / Boja</Text>
              </View>
              <View style={styles.tableColPaketi}>
                <Text style={styles.th}>Paketi</Text>
              </View>
              <View style={styles.tableColNapomena}>
                <Text style={styles.th}>Napomena</Text>
              </View>
              <View style={styles.tableColIstovar}>
                <Text style={styles.th}>Istovar</Text>
              </View>
            </View>

            {data.roba?.map((item: any, index: number) => (
              <View key={index} style={styles.tableRow}>
                <View style={styles.tableColNalog}>
                  <Text style={styles.td}>{getValue(item, ['brojNaloga', 'BrojNaloga'])}</Text>
                </View>
                <View style={styles.tableColStavka}>
                  <Text style={styles.td}>
                    {getValue(item, ['stavka', 'Stavka', 'StavkaNaloga', 'stavkaNaloga'])}
                  </Text>
                </View>
                <View style={styles.tableColPonuda}>
                  <Text style={styles.td}>{getValue(item, ['brojPonude', 'BrojPonude'])}</Text>
                </View>
                <View style={styles.tableColSifra}>
                  <Text style={styles.td}>{getSifraValue(item)}</Text>
                  <Text style={styles.td}>{getBojaValue(item)}</Text>
                </View>
                <View style={styles.tableColPaketi}>
                  <Text style={styles.td}>
                    {Array.isArray(item.paketi)
                      ? item.paketi.join(', ')
                      : getValue(item, ['brojPaketa', 'BrojPaketa'])}
                  </Text>
                </View>
                <View style={styles.tableColNapomena}>
                  <Text style={styles.td}>{getValue(item, ['napomena', 'Napomena'])}</Text>
                </View>
                <View style={styles.tableColIstovar}>
                  <Text style={styles.td}>{getValue(item, ['istovar', 'Istovar'])}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.attentionBox}>
            <Text style={styles.attentionTitle}>DODATNE INFORMACIJE ZA IZVOZ</Text>
            <View style={styles.row}>
              <View style={styles.col4Left}>
                <PdfField
                  label='Dodatni utovar robe'
                  value={getDodatniUtovar(getValue(data, ['DodatniUtovar', 'dodatniUtovarRobe']))}
                />
              </View>
              <View style={styles.col4Middle}>
                <PdfField
                  label='Broj naloga za dodatni utovar robe'
                  value={String(getValue(data, ['DodatniUtovarNalozi', 'brojNalogaDodatniUtovarRobe']))}
                  valueStyle={styles.exportValue}
                />
              </View>
              <View style={styles.col4Right}>
                <PdfField
                  label='Carinski postupak'
                  value={data.carisnkiPostupak ?? data.CarisnkiPostupak}
                  valueStyle={styles.exportValue}
                />
              </View>
            </View>
            <View style={styles.row}>
              <View style={styles.col4Left}>
                <PdfField
                  label='Fakturisati transport'
                  value={fakturisatiTransport === 'DA' ? 'DA' : '0'}
                />
              </View>
              <View style={styles.col4Middle}>
                <PdfField
                  label='Broj naloga za fakturisanje transporta'
                  value={fakturisatiTransportNalog}
                  valueStyle={styles.exportValue}
                />
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>DODATNE NAPOMENE</Text>
          <View style={styles.notesBox}>
            <Text>{data.napomene || '-'}</Text>
          </View>
        </View>

        <Text style={styles.footer}>Alternativa d.o.o. - Najava utovara</Text>
      </Page>
    </Document>
  )
}

export default NajavaUtovaraPdf
